if (!customElements.get('searchable-select')) {
  customElements.define(
    'searchable-select',
    class SearchableSelect extends HTMLElement {
      constructor() {
        super();
        this.isOpen = false;
        this.activeIndex = -1;
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      }

      connectedCallback() {
        this.select = this.querySelector('select');
        if (!this.select) return;

        this.placeholder =
          this.dataset.placeholder ||
          this.select.querySelector('option[value=""]')?.textContent?.trim() ||
          'Selecciona una opción';

        this.build();
        this.bindEvents();
        this.syncFromSelect();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
      }

      build() {
        const selectWrapper = this.querySelector('.select');
        if (!selectWrapper) return;

        this.select.classList.add('searchable-select__native', 'visually-hidden');
        this.select.setAttribute('tabindex', '-1');

        this.trigger = document.createElement('button');
        this.trigger.type = 'button';
        this.trigger.className = 'searchable-select__trigger select__select';
        this.trigger.setAttribute('aria-haspopup', 'listbox');
        this.trigger.setAttribute('aria-expanded', 'false');

        this.panel = document.createElement('div');
        this.panel.className = 'searchable-select__panel';
        this.panel.hidden = true;

        this.searchInput = document.createElement('input');
        this.searchInput.type = 'search';
        this.searchInput.className = 'searchable-select__search field__input';
        this.searchInput.placeholder = this.dataset.searchPlaceholder || 'Buscar...';
        this.searchInput.setAttribute('autocomplete', 'off');
        this.searchInput.setAttribute('autocorrect', 'off');
        this.searchInput.setAttribute('spellcheck', 'false');
        this.searchInput.setAttribute('role', 'combobox');
        this.searchInput.setAttribute('aria-autocomplete', 'list');

        this.list = document.createElement('ul');
        this.list.className = 'searchable-select__list list-unstyled';
        this.list.setAttribute('role', 'listbox');

        this.options = Array.from(this.select.options).map((option, index) => {
          const item = document.createElement('li');
          item.className = 'searchable-select__option';
          item.setAttribute('role', 'option');
          item.dataset.value = option.value;
          item.dataset.index = String(index);
          item.textContent = option.textContent.trim();

          if (!option.value) {
            item.classList.add('searchable-select__option--placeholder');
            item.hidden = true;
          }

          this.list.appendChild(item);
          return item;
        });

        this.emptyState = document.createElement('p');
        this.emptyState.className = 'searchable-select__empty caption';
        this.emptyState.textContent = this.dataset.emptyText || 'No se encontraron resultados';
        this.emptyState.hidden = true;

        this.panel.append(this.searchInput, this.list, this.emptyState);
        selectWrapper.prepend(this.trigger);
        selectWrapper.appendChild(this.panel);

        this.triggerId = `searchable-select-trigger-${Math.random().toString(36).slice(2, 9)}`;
        this.listId = `searchable-select-list-${Math.random().toString(36).slice(2, 9)}`;
        this.trigger.id = this.triggerId;
        this.list.id = this.listId;
        this.searchInput.setAttribute('aria-controls', this.listId);
        this.trigger.setAttribute('aria-controls', this.listId);
      }

      bindEvents() {
        this.trigger.addEventListener('click', () => {
          this.isOpen ? this.close() : this.open();
        });

        this.searchInput.addEventListener('input', () => {
          this.filterOptions();
          this.activeIndex = -1;
          this.updateActiveOption();
        });

        this.searchInput.addEventListener('keydown', (event) => this.onSearchKeydown(event));

        this.list.addEventListener('click', (event) => {
          const option = event.target.closest('.searchable-select__option');
          if (!option || option.hidden || option.classList.contains('is-hidden')) return;
          this.chooseOption(option);
        });

        this.select.addEventListener('change', () => this.syncFromSelect());

        this.select.form?.addEventListener('invalid', () => {
          this.trigger.classList.add('searchable-select__trigger--invalid');
        });

        this.select.form?.addEventListener('submit', () => {
          if (!this.select.checkValidity()) {
            this.trigger.classList.add('searchable-select__trigger--invalid');
          }
        });
      }

      getVisibleOptions() {
        return this.options.filter(
          (option) => !option.hidden && !option.classList.contains('is-hidden') && option.dataset.value
        );
      }

      filterOptions() {
        const query = this.searchInput.value.trim().toLowerCase();
        let visibleCount = 0;

        this.options.forEach((option) => {
          if (!option.dataset.value) return;

          const matches = !query || option.textContent.toLowerCase().includes(query);
          option.classList.toggle('is-hidden', !matches);
          if (matches) visibleCount += 1;
        });

        this.emptyState.hidden = visibleCount > 0;
      }

      chooseOption(option) {
        const value = option.dataset.value;
        this.select.value = value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncFromSelect();
        this.close();
        this.trigger.focus();
      }

      syncFromSelect() {
        const selectedOption = this.select.selectedOptions[0];
        const label = selectedOption?.value ? selectedOption.textContent.trim() : this.placeholder;

        this.trigger.textContent = label;
        this.trigger.classList.toggle('searchable-select__trigger--placeholder', !this.select.value);
        this.trigger.classList.remove('searchable-select__trigger--invalid');

        this.options.forEach((option) => {
          const isSelected = option.dataset.value === this.select.value && !!this.select.value;
          option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          option.classList.toggle('is-selected', isSelected);
        });
      }

      open() {
        this.isOpen = true;
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        this.searchInput.value = '';
        this.filterOptions();
        this.activeIndex = -1;
        this.updateActiveOption();
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
        requestAnimationFrame(() => this.searchInput.focus());
      }

      close() {
        this.isOpen = false;
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
        this.activeIndex = -1;
        this.updateActiveOption();
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.close();
      }

      onDocumentKeydown(event) {
        if (!this.isOpen) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          this.trigger.focus();
        }
      }

      onSearchKeydown(event) {
        const visibleOptions = this.getVisibleOptions();

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.activeIndex = Math.min(this.activeIndex + 1, visibleOptions.length - 1);
          this.updateActiveOption(visibleOptions);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.activeIndex = Math.max(this.activeIndex - 1, 0);
          this.updateActiveOption(visibleOptions);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          const activeOption = visibleOptions[this.activeIndex];
          if (activeOption) this.chooseOption(activeOption);
        } else if (event.key === 'Tab') {
          this.close();
        }
      }

      updateActiveOption(visibleOptions = this.getVisibleOptions()) {
        this.options.forEach((option) => option.classList.remove('is-active'));

        if (this.activeIndex < 0 || !visibleOptions[this.activeIndex]) return;

        const activeOption = visibleOptions[this.activeIndex];
        activeOption.classList.add('is-active');
        activeOption.scrollIntoView({ block: 'nearest' });
      }
    }
  );
}
